/**
 * 48コマ表の列幅と、そこから決まる「表カードの自然幅」。
 *
 * ## 列幅は「最大値 + セル padding」で決める
 *
 * 各列幅は Chromium 実測 (14px / DS の font stack
 * `"Helvetica Neue", Arial, "Hiragino Kaku Gothic ProN", ...`) に基づく。
 * 基準は **業務上の最大表示値 `99,999,999`** (デザイナー指定 #6668) と列ヘッダーの
 * どちらか広いほうで、これに `bodyCellStyle` / `headerCellStyle` の左右 padding
 * 合計 8px を足した値を採る。
 *
 * | 列 | 最大内容 (実測) | ヘッダー (実測) | 採用 |
 * |---|---|---|---|
 * | 時刻 | `00:00〜` 49.04 | 時刻 28 | 58 |
 * | 需要想定値 | `99,999,999` 70.07 | 需要想定値 70 | 78 |
 * | 保存済みとの差 | `▲99,999,999` 83.93 | 98 (折返し可) | 92 |
 * | 当日実績 | `99,999,999` (12px) 60.07 | 当日実績 56 (折返し可) | 68 |
 * | 実績との差分 | `▲99,999,999` 83.93 | 実績との差分 84 | 92 |
 * | 参照列 | `99,999,999` (12px) 60.07 | `過去実績 08/27` 96.24 | 104 |
 *
 * 差分列は符号 (`+` / `▲`) が付くぶん値列より広い。当日実績・参照列のセルは 12px
 * だがヘッダーは 14px なので、そちらが幅を決めている。
 *
 * ## なぜ「自然幅」が要るのか
 *
 * 表は `TableRoot width="100%"` + `table-layout: auto` なので、**カードが列幅の合計
 * より広いと余剰が各列へ配分されて列が間延びする**。つまり列幅を詰めても、カード幅が
 * 固定のままでは見た目は変わらない (グラフも広がらない)。
 *
 * proto (`demand/forecast-today.html`) はカード幅の既定を「表を max-content で実測した
 * 必要幅」にしており、dev 側はそれを持たず 480px 固定で代用していた
 * (`useResizableTableWidth` の旧 [design 差異] コメント)。ここでその欠けを埋める。
 * 実測ではなく列幅定数の総和で出すのは、レイアウト依存 (カード幅 → 実測 → カード幅 の
 * 循環) を作らず、列の増減にも決定的に追随できるため。
 */
import type { KomaSlots } from '~/pages/DemandForecastDaily/models/types'

/** 列幅 (px)。内訳は本ファイル冒頭の表を参照 */
export const KOMA_COLUMN_WIDTH = {
  time: 58,
  draftValue: 78,
  saveDiff: 92,
  actual: 68,
  actualDiff: 92,
  reference: 104,
} as const

/**
 * 表カードが列の外側に持つ横幅 (px)。
 * デスクトップ (>=1200px) の `padding: 12px 8px` × 左右 + `borderWidth: 1px` × 左右。
 */
export const KOMA_TABLE_CARD_CHROME = 18

/**
 * 参照列 (過去実績 / 確定履歴) の防御的上限。呼び出し元が過去実績 5 件と確定履歴
 * 5 件を concat して渡すため、合算の 10 件で切る (KomaGrid の cap と同値)。
 */
export const MAX_REFERENCE_COLUMNS = 10

/**
 * 当日断面 (実績 / 実績との差分の 2 列を出す条件)。
 *
 * 「prop が渡っている」だけでは足りない。全コマ undefined の配列が渡ることがあり、
 * その時に列を出すと中身が全部「—」の列が 2 本増える。
 */
export function hasTodayActuals(
  todayActualSlots: KomaSlots | undefined,
): boolean {
  return (
    todayActualSlots !== undefined &&
    todayActualSlots.some((v) => v !== undefined)
  )
}

/**
 * 今の列構成で表がちょうど収まるカード幅 (px)。表カード幅の既定値に使う。
 *
 * 参照列が増減すると自然幅も変わる。ユーザーが自分で幅を決めていない間は
 * これに追随させる (= 参照列を採用した瞬間に横スクロールが出ない)。
 */
export function naturalKomaTableCardWidth({
  todayActualSlots,
  referenceColumnCount,
}: {
  todayActualSlots: KomaSlots | undefined
  referenceColumnCount: number
}): number {
  const columns =
    KOMA_COLUMN_WIDTH.time +
    KOMA_COLUMN_WIDTH.draftValue +
    KOMA_COLUMN_WIDTH.saveDiff +
    (hasTodayActuals(todayActualSlots)
      ? KOMA_COLUMN_WIDTH.actual + KOMA_COLUMN_WIDTH.actualDiff
      : 0) +
    Math.min(Math.max(referenceColumnCount, 0), MAX_REFERENCE_COLUMNS) *
      KOMA_COLUMN_WIDTH.reference

  return columns + KOMA_TABLE_CARD_CHROME
}
